import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Button from '../../components/button'
import meteorBlue from '../../assets/images/meteor-blue.svg'
import star from '../../assets/images/star.svg'
import Alert from '../../components/alert'
import { setShowIntroJs, setScore } from '../../redux/mastermind/reducer'
import { withRouter } from 'react-router-dom'
import { Steps } from 'intro.js-react'
import 'intro.js/introjs.css'

const allowedDigits = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0]
const targetNumberDigitCount = 4
const correctAnswerScore = 10
const gameStatuses = {
  PLAYING: 'playing',
  CORRECT_ANSWER: 'correctAnswer',
  GIVE_UP: 'giveUp',
  INVALID_INPUT: 'invalidInput',
  END_GAME_SUMMARY: 'endGameSummary'
}
const gameResultDefaults = {
  status: gameStatuses.PLAYING,
  starCount: 0,
  score: 0,
  answer: 0
}
const _steps = [
  {
    element: '.user-input-buttons',
    intro:
      'You can use the keyboard or mouse to write an arithmetic expression in the input box.',
    position: 'top'
  },
  {
    element: '#ClearUserInput',
    intro:
      'You can click on the <span class="custom-button primary"><i class="fas fa-times"></i></span> button to clear the entire input',
    position: 'left'
  },
  {
    element: '#RemoveLastUserInput',
    intro:
      'You can press "Backspace" or click on the <span class="custom-button primary"><i class="fas fa-arrow-left"></i></span> button to erase the last operator or the number in your input.',
    position: 'left'
  },
  {
    element: '#CheckResults',
    intro:
      'When you are done with the input, you can press "Enter" or click on the <span class="custom-button primary">Check Result</span> button',
    position: 'top'
  },
  {
    intro: (
      <>
        Stars:
        <br />
        If your score is 9 or 10, you will see{' '}
        <img src={star} width='13' height='13' alt='star' />
        <img src={star} width='13' height='13' alt='star' className='ms-1' />
        <img src={star} width='13' height='13' alt='star' className='ms-1' />
        <br />
        If your score is 6,7 or 8, you will see{' '}
        <img src={star} width='13' height='13' alt='star' />
        <img src={star} width='13' height='13' alt='star' className='ms-1' />
        <br />
        If your score is 1 through 5, you will see{' '}
        <img src={star} width='13' height='13' alt='star' />
      </>
    )
  }
]

const Single = props => {
  const dispatch = useDispatch()
  const inputRef = useRef()
  const { score, showIntroJs } = useSelector(state => state.mastermind)
  const [userInput, setUserInput] = useState([])
  const [userGuesses, setUserGuesses] = useState([])
  const [targetNumber, setTargetNumber] = useState(null)
  const [gameResult, setGameResult] = useState(gameResultDefaults)
  const [introJsSteps, setIntroJsSteps] = useState(null)
  const [showRules, setShowRules] = useState(false)

  const handleToggleRules = () => {
    setShowRules(!showRules)
  }

  const handleShowIntroJs = () => {
    dispatch(setShowIntroJs(true))
  }

  const handleHideIntroJs = () => {
    dispatch(setShowIntroJs(false))
    setIntroJsSteps(null)
  }

  const handleExampleUserInputs = () => {
    const _userInput = generateNumber()
    setUserInput(_userInput.split(''))
  }

  useEffect(() => {
    if (showIntroJs) {
      if (userInput.length === 0 && userGuesses.length === 0)
        handleExampleUserInputs()
      setIntroJsSteps(_steps)
    }
    // eslint-disable-next-line
  }, [showIntroJs, userInput])

  const generateNumber = () => {
    let digits = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
    let number = ''
    for (let i = 0; i < targetNumberDigitCount; i++) {
      let digit = null
      let digitIndex
      if (i === 0)
        digitIndex = Math.floor(Math.random() * (digits.length - 1)) + 1
      else digitIndex = Math.floor(Math.random() * (digits.length - 1))

      digit = digits.splice(digitIndex, 1)
      number += digit
    }
    return number
  }

  const generateTargetNumber = () => {
    const _targetNumber = generateNumber()
    setTargetNumber(_targetNumber)
  }

  const handleSetUserInput = digit => event => {
    if (userInput.length < 4) {
      setUserInput([...userInput, digit])
    }

    event.currentTarget.blur()
  }

  const newGame = () => {
    resetGame()
  }

  const continueToPlay = () => {
    if (gameResult.status === gameStatuses.INVALID_INPUT) {
      setGameResult({
        ...gameResult,
        status: gameStatuses.PLAYING
      })
    }
  }

  const resetGame = () => {
    setGameResult(gameResultDefaults)
    setUserInput([])
    setUserGuesses([])
    generateTargetNumber()
  }

  const handleClearUserInput = () => {
    setUserInput([])
  }

  const handleRemoveLastUserInput = () => {
    if (userInput.length > 0) {
      let _userInput = [...userInput]
      _userInput.pop()
      setUserInput(_userInput)
    }
  }

  const validateUserInputs = () => {
    let valid = true
    userInput.forEach(input => {
      if (!(input >= 0 && input <= 9)) valid = false
    })
    return valid
  }

  const handleAnswer = guess => {
    let correctPlace = []
    let wrongPlace = 0
    for (let i = 0; i < 4; i++) {
      if (guess[i] === targetNumber[i]) correctPlace.push(guess[i])
    }
    for (let i = 0; i < 4; i++) {
      if (
        correctPlace.indexOf(guess[i]) === -1 &&
        targetNumber.indexOf(guess[i]) !== -1
      )
        wrongPlace++
    }
    return {
      correctPlace: correctPlace.length,
      wrongPlace
    }
  }

  const calculateScore = () => {
    const userGuessesCount = userGuesses.length + 1
    if (userGuessesCount <= 7) return correctAnswerScore
    else if (userGuessesCount >= 16) return 1
    else return correctAnswerScore - (userGuessesCount - 7)
  }

  const handleCheckUserInput = () => {
    if (validateUserInputs()) {
      const userGuess = userInput.join('')
      if (userGuess === targetNumber) {
        setUserGuesses([
          {
            guess: userGuess,
            correctPlace: 4,
            wrongPlace: 0
          },
          ...userGuesses
        ])
        const userScore = calculateScore()
        const starCount = userScore > 8 ? 3 : userScore > 5 ? 2 : 1
        setGameResult({
          status: gameStatuses.CORRECT_ANSWER,
          score: userScore,
          starCount: starCount
        })
        dispatch(setScore(score + userScore))
      } else {
        const answer = handleAnswer(userGuess)
        setUserGuesses([
          {
            guess: userGuess,
            ...answer
          },
          ...userGuesses
        ])
        setUserInput([])
      }
    } else {
      setGameResult({
        status: gameStatuses.INVALID_INPUT
      })
    }
  }

  const handleGiveUp = () => {
    setGameResult({
      ...gameResultDefaults,
      status: gameStatuses.GIVE_UP
    })
  }

  const endGameSummary = () => {
    setGameResult({
      ...gameResultDefaults,
      status: gameStatuses.END_GAME_SUMMARY
    })
    setUserInput([])
  }

  const handleButtonDisabled = digit => {
    if (gameResult.status === gameStatuses.END_GAME_SUMMARY) return true
    if (userInput.length === 0 && digit === 0) return true
    const isExists = userInput.findIndex(input => input === digit)
    if (isExists !== -1) return true
    return false
  }

  const validateKeyPress = event => {
    const key = parseInt(event.key)

    if (
      userInput.length < 4 &&
      !handleButtonDisabled(key) &&
      key >= 0 &&
      key <= 9
    )
      return true
    return false
  }

  const handleWindowKeyDown = event => {
    event.preventDefault()

    if (event.keyCode === 27) handleClearUserInput()
    else if (event.keyCode === 13 && userInput.length === 4) {
      handleCheckUserInput()
    } else if (event.keyCode === 8) handleRemoveLastUserInput()
    else if (validateKeyPress(event)) {
      setUserInput([...userInput, parseInt(event.key)])
    }
  }

  useEffect(() => {
    resetGame()
    return () => {}
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    window.addEventListener('keydown', handleWindowKeyDown)
    return () => {
      window.removeEventListener('keydown', handleWindowKeyDown)
    }
    // eslint-disable-next-line
  }, [userInput, gameResult])

  const UserGuess = ({ userGuess }) => {
    return (
      <div className='guess-container'>
        <span className='guess'>{userGuess.guess}</span>
        <span className='answer'>
          {userGuess.correctPlace > 0 && (
            <span className='badge rounded-pill bg-success'>
              +{userGuess.correctPlace}
            </span>
          )}
          {userGuess.wrongPlace > 0 && (
            <span className='badge rounded-pill bg-warning'>
              -{userGuess.wrongPlace}
            </span>
          )}
          {userGuess.correctPlace === 0 && userGuess.wrongPlace === 0 && (
            <span className='badge rounded-pill bg-danger'>0</span>
          )}
        </span>
      </div>
    )
  }

  return (
    <div className='master-mind game-play d-flex justify-content-center align-items-center align-self-center text-center h-100'>
      <div className='header d-flex justify-content-between w-100'>
        <div style={{ width: 180, textAlign: 'left', display: 'flex' }}>
          <div className='text-bg user-score' title='Score'>
            <img src={star} width='24' height='24' alt='star' />
            {score}
          </div>
        </div>
        <div className='title d-flex justify-content-center'>
          <img src={meteorBlue} width='36' height='36' alt='meteor' />
          <h1 className='title'>MasterMind</h1>
        </div>
        <div style={{ width: 180 }}>
          <Button
            color='primary'
            className='me-2'
            title='Show Game Info'
            onClick={handleToggleRules}
          >
            <i className='fas fa-info'></i>
          </Button>
          <Button
            onClick={handleShowIntroJs}
            className='me-2'
            title='How to Play'
          >
            <i className='fas fa-question'></i>
          </Button>
          <Button to='/mastermind' title='Close Game'>
            <i className='fas fa-times'></i>
          </Button>
        </div>
      </div>

      <div className='play-area' ref={inputRef}>
        <div className='user-input-area'>
          <h2>Your Guess</h2>
          <div className='text-bg'>
            {userGuesses.length > 0 && <UserGuess userGuess={userGuesses[0]} />}
          </div>
          <div className='text-bg-2'>
            <span className='user-input'>{userInput.map(input => input)}</span>
            {userInput.length > 0 && (
              <div className='buttons'>
                <Button
                  onClick={handleRemoveLastUserInput}
                  className='remove-last-user-input'
                  id='RemoveLastUserInput'
                >
                  <i className='fas fa-arrow-right'></i>
                </Button>
                <Button onClick={handleClearUserInput} id='ClearUserInput'>
                  <i className='fas fa-times'></i>
                </Button>
              </div>
            )}
          </div>

          <div className='user-input-buttons mt-3'>
            {allowedDigits.map((digit, index) => (
              <Button
                onClick={handleSetUserInput(digit)}
                key={index}
                disabled={handleButtonDisabled(digit)}
              >
                {digit}
              </Button>
            ))}
          </div>
          <div className='d-flex w-100 justify-content-center mt-1'>
            {userInput.length === 4 && (
              <Button onClick={handleCheckUserInput} id='CheckResults'>
                Check Result
              </Button>
            )}
            {userGuesses.length > 20 &&
              !gameResult.status !== gameStatuses.END_GAME_SUMMARY && (
                <Button onClick={handleGiveUp} id='GiveUp'>
                  Give Up
                </Button>
              )}

            {gameResult.status === gameStatuses.END_GAME_SUMMARY && (
              <Button onClick={newGame}>New Game</Button>
            )}
          </div>
        </div>

        <div className='user-guesses'>
          <h2>Guesses ({userGuesses.length})</h2>
          <ol className='guesses-container'>
            {userGuesses.map((userGuess, index) => {
              return (
                <li key={index}>
                  <UserGuess userGuess={userGuess} />
                </li>
              )
            })}
          </ol>
        </div>

        <Alert
          show={gameResult.status === gameStatuses.CORRECT_ANSWER}
          text={'Congratulations!'}
          starCount={gameResult.starCount}
          score={gameResult.score}
          buttons={[
            {
              text: 'Got It!',
              onClick: endGameSummary,
              color: 'primary'
            }
          ]}
        />
        <Alert
          show={gameResult.status === gameStatuses.GIVE_UP}
          text={'Target Number: ' + targetNumber}
          starCount={gameResult.starCount}
          score={gameResult.score}
          buttons={[
            {
              text: 'New Game!',
              onClick: newGame,
              color: 'primary'
            }
          ]}
        />
        <Alert
          show={gameResult.status === gameStatuses.INVALID_INPUT}
          text={'Oops...check your operations!'}
          starCount={gameResult.starCount}
          score={gameResult.score}
          buttons={[
            {
              text: 'Continue',
              onClick: continueToPlay,
              color: 'primary'
            }
          ]}
        />
        <Alert
          show={showRules}
          html={
            <div style={{ maxWidth: 500 }}>
              <h5>Objective</h5>
              <p>Find the hidden number in as few tries as you can</p>
              <h5>Rules</h5>
              <p>
                In each try, you guess a number and you will get clues
                <br />
                The feedback tells you the number of matching digits and if they
                have been used in correct places or not. For example, +2 -1
                means 3 digits of your guess appear in the hidden number, 2 of
                them in the correct places and 1 of them in a wrong place.
                <br />
              </p>
              <h5>Scoring </h5>
              <p>
                10 pts for finding in up to 7 guesses
                <br />
                9 pts for finding in 8 guesses
                <br />
                8 pts for finding in 9 guesses
                <br />
                ...
                <br />
                1 pt for finding in 16 or more guesses
                <br />
                No points if “give up”
                <br />
              </p>
            </div>
          }
          buttons={[
            {
              text: 'Close',
              onClick: handleToggleRules,
              color: 'primary'
            }
          ]}
        />
      </div>

      {showIntroJs && introJsSteps !== null && (
        <Steps
          enabled={showIntroJs}
          steps={introJsSteps}
          initialStep={0}
          onExit={handleHideIntroJs}
          options={{
            hidePrev: true,
            hideNext: true,
            doneLabel: 'Done',
            nextToDone: true
          }}
        />
      )}
    </div>
  )
}

export default withRouter(Single)
